import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { SYMBOLS, PAY_TABLE, FREE_SPINS_TABLE, PAYLINES, PAYLINE_COLORS, type SlotSymbol } from '../utils/slotsLogic'

interface PayTableProps {
  betPerLine: number
  isOpen: boolean
  onClose: () => void
}

export function PayTable({ betPerLine, isOpen, onClose }: PayTableProps) {
  const symbolOrder: SlotSymbol[] = ['wild', 'scatter', 'gun', 'dice', 'cards', 'diamond', 'whiskey', 'cigar']

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={cn(
              'relative w-full max-w-2xl max-h-[90vh] overflow-y-auto',
              'bg-gradient-to-b from-gray-800 to-gray-900',
              'border-2 border-casino-gold/50 rounded-xl',
              'p-4 md:p-6'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-2 right-2 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>

            <h2 className="text-2xl font-bold text-casino-gold text-center mb-6">
              TABLA DE PAGOS
            </h2>

            {/* Símbolos y pagos */}
            <div className="space-y-3">
              {symbolOrder.map((symbolId) => {
                const symbol = SYMBOLS[symbolId]
                const pays = PAY_TABLE[symbolId]

                return (
                  <div
                    key={symbolId}
                    className={cn(
                      'flex items-center gap-4 p-3 rounded-lg',
                      'bg-black/30 border border-gray-700'
                    )}
                  >
                    {/* Símbolo */}
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <span className="text-3xl">{symbol.emoji}</span>
                      <div>
                        <div className="font-semibold text-white">{symbol.name}</div>
                        {symbol.isWild && (
                          <div className="text-xs text-casino-gold">Sustituye todos</div>
                        )}
                        {symbol.isScatter && (
                          <div className="text-xs text-green-400">+ Free Spins</div>
                        )}
                      </div>
                    </div>

                    {/* Pagos */}
                    <div className="flex-1 flex justify-end gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-gray-500">×3</div>
                        <div className="text-white font-bold">
                          ${(pays[2] * betPerLine).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">×4</div>
                        <div className="text-white font-bold">
                          ${(pays[3] * betPerLine).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500">×5</div>
                        <div className="text-casino-gold font-bold">
                          ${(pays[4] * betPerLine).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Free Spins */}
            <div className="mt-6 p-4 rounded-lg bg-green-900/30 border border-green-600/50">
              <h3 className="text-lg font-bold text-green-400 mb-2">FREE SPINS</h3>
              <div className="flex justify-around text-center">
                {Object.entries(FREE_SPINS_TABLE).map(([count, spins]) => (
                  <div key={count}>
                    <div className="text-2xl mb-1">💰 ×{count}</div>
                    <div className="text-white font-bold">{spins} Giros</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Los scatters pagan en cualquier posición
              </p>
            </div>

            {/* Líneas de pago */}
            <div className="mt-6">
              <h3 className="text-lg font-bold text-casino-gold mb-3">LÍNEAS DE PAGO</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {PAYLINES.map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 rounded bg-black/30"
                  >
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: PAYLINE_COLORS[index] }}
                    />
                    <span className="text-sm text-gray-300">Línea {index + 1}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info adicional */}
            <div className="mt-6 text-xs text-gray-500 text-center space-y-1">
              <p>🎩 Wild sustituye todos los símbolos excepto 💰 Scatter</p>
              <p>Las ganancias se calculan de izquierda a derecha</p>
              <p>Solo se paga la ganancia más alta por línea</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
